import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { Smartphone, X, Share, Plus, ScanLine, GripHorizontal } from "lucide-react";

const isInstalledPWA = () =>
  (typeof window !== "undefined") &&
  (window.navigator?.standalone === true ||
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    (window.matchMedia && window.matchMedia("(display-mode: fullscreen)").matches));

export const InstallTab = () => {
  const [open, setOpen] = useState(false);
  const [installed, setInstalled] = useState(isInstalledPWA());
  const constraintsRef = useRef(null);
  // Canonical public URL — robust even when the app is previewed inside an iframe.
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const url = (envUrl && /^https?:\/\//.test(envUrl))
    ? envUrl.replace(/\/$/, "")
    : (typeof window !== "undefined" ? window.location.origin : "");

  useEffect(() => {
    const mql = window.matchMedia ? window.matchMedia("(display-mode: standalone)") : null;
    const update = () => setInstalled(isInstalledPWA());
    update();
    if (mql?.addEventListener) mql.addEventListener("change", update);
    window.addEventListener("visibilitychange", update);
    return () => {
      if (mql?.removeEventListener) mql.removeEventListener("change", update);
      window.removeEventListener("visibilitychange", update);
    };
  }, []);

  // Already installed / launched from the Home Screen — no need to nudge an install.
  if (installed) return null;

  return (
    <>
      <button
        data-testid="install-tab-btn"
        onClick={() => setOpen(true)}
        title="Install on iPhone / iPad"
        className="fixed bottom-5 left-5 z-40 flex items-center gap-2 rounded-full glass px-4 py-2.5 text-sm text-white/90 transition hover:border-cyan-400/40 hover:text-cyan-300"
      >
        <Smartphone size={16} />
        <span className="font-tac text-[10px] tracking-widest">GET APP</span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)} className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm" data-testid="install-overlay" />
            <div ref={constraintsRef} className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                drag
                dragConstraints={constraintsRef}
                dragMomentum={false}
                dragElastic={0.08}
                initial={{ opacity: 0, scale: 0.92, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.92, y: 20 }}
                transition={{ type: "spring", damping: 24, stiffness: 260 }}
                className="pointer-events-auto max-h-[90vh] w-[92%] max-w-md overflow-y-auto rounded-3xl glass p-7"
                data-testid="install-modal"
              >
              <div className="mb-2 flex cursor-grab items-center justify-center gap-1.5 text-white/30 active:cursor-grabbing" data-testid="install-drag-handle">
                <GripHorizontal size={16} />
                <span className="font-tac text-[8px] tracking-widest">DRAG TO MOVE</span>
              </div>
              <button onClick={() => setOpen(false)} data-testid="install-close" className="absolute right-5 top-5 text-white/50 hover:text-white"><X size={20} /></button>

              <p className="font-tac text-[10px] tracking-[0.3em] txt-cyan">ADD TO HOME SCREEN</p>
              <h2 className="font-head mt-1 text-3xl font-black tracking-tighter text-white">Install OCTON</h2>
              <p className="mt-1 text-xs text-white/50">Scan with your iPhone or iPad camera to launch OCTON, then add it to your Home Screen.</p>

              <div className="mt-5 flex justify-center">
                <div className="relative rounded-2xl bg-white p-4">
                  <QRCodeCanvas value={url} size={208} bgColor="#ffffff" fgColor="#050505" level="M" includeMargin={false} data-testid="install-qr" />
                  <ScanLine className="absolute -right-3 -top-3 rounded-full bg-cyan-400 p-1 text-black" size={26} />
                </div>
              </div>
              <p className="mt-2 break-all text-center font-mono text-[10px] text-white/40">{url}</p>

              <div className="mt-5 space-y-3 rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <div className="flex items-center gap-2 text-xs text-white/70">
                  <Smartphone size={15} className="txt-cyan" />
                  <span className="font-semibold text-white">iPhone / iPad (Safari)</span>
                </div>
                <ol className="space-y-2 text-xs text-white/60">
                  <li className="flex items-center gap-2"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-cyan-400/15 text-[10px] text-cyan-300">1</span> Scan the code — it opens OCTON in Safari</li>
                  <li className="flex items-center gap-2"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-cyan-400/15 text-[10px] text-cyan-300">2</span> Tap the <Share size={13} className="inline" /> Share button</li>
                  <li className="flex items-center gap-2"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-cyan-400/15 text-[10px] text-cyan-300">3</span> Choose <Plus size={13} className="inline" /> "Add to Home Screen"</li>
                </ol>
                <p className="border-t border-white/8 pt-2 text-[11px] text-white/40">
                  Android: scan → open in Chrome → menu → "Install app". Works offline once installed — the neural brain stays available even on a network drop.
                </p>
              </div>
            </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
